import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Dropdown renderizado en un portal a document.body, posicionado con
// `position: fixed` según el rectángulo del botón que lo abre (anchorRef) — así
// nunca lo recorta un ancestro con overflow-hidden/overflow-y-auto. Ese recorte
// (no un z-index insuficiente) era la causa real de dropdowns personalizados que
// "se ven cortados/detrás" dentro de paneles o listas con scroll (ej. asignar
// ryder en AdminPanel.jsx y CompanyDispatch.jsx).
export default function DropdownPortal({ open, anchorRef, onClose, align = "right", className = "", children }) {
  const popupRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const update = () => {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({
        top: r.bottom + 4,
        left: align === "left" ? r.left : undefined,
        right: align === "right" ? window.innerWidth - r.right : undefined,
        minWidth: r.width,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef, align]);

  // Cierre al hacer click afuera — revisa tanto el botón ancla como el popup
  // (que ahora vive en otra parte del DOM por el portal, así que un click-outside
  // "normal" basado en un solo ref padre no lo detectaría correctamente).
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (anchorRef.current?.contains(e.target) || popupRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, anchorRef, onClose]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={popupRef}
      className={`fixed z-[10060] ${className}`}
      style={{ top: pos.top, left: pos.left, right: pos.right, minWidth: pos.minWidth }}
    >
      {children}
    </div>,
    document.body,
  );
}
